import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import * as dh from '../../helpers/dates';
import * as eh from '../../helpers/event-helper';
import { useEventCallback } from '../../helpers/hooks';
import * as utils from '../../helpers/mobile';
import styles from './styles';
import {
  Table,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import TableTotalCell from './TableTotalCell';
import TableCell from './TableCell';
import TableRowPlaceholder from './TableRowPlaceholder';

const useStyles = makeStyles(styles);

const InboundStatsTable = ({ events }) => {
  const classes = useStyles();
  const [selectedRow, setSelectedRow] = useState(null);
  const isPortrait = utils.isPortrait();

  const total = useMemo(() => {
    return events.reduce(
      (result, event) => {
        const { inbound } = event;
        result.pax += eh.getTotalPaxLoad(inbound.paxLoad);
        result.cargoWeight += eh.getTotalCargoWeight(inbound.cargo);
        return result;
      },
      {
        pax: 0,
        cargoWeight: 0,
      }
    );
  }, [events]);

  const handleRowClick = useEventCallback(event => {
    setSelectedRow(event.currentTarget.getAttribute('data-eventid'));
  });

  return (
    <>
      <Table
        size="small"
        stickyHeader
        className={classNames(classes.leftTable, {
          [classes.fixedTable]: !isPortrait,
        })}
      >
        <TableHead>
          <TableRow className={classes.tableHead}>
            <TableCell>Date/STA</TableCell>
            <TableCell>Airline</TableCell>
            <TableCell>Flight Number</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!events.length && <TableRowPlaceholder />}
          {events.map(event => {
            return (
              <TableRow
                selected={event._id === selectedRow}
                key={event._id}
                data-eventid={event._id}
                onClick={handleRowClick}
              >
                <TableCell>{dh.dateToDateTimeString(event.STA)}</TableCell>
                <TableCell>{event.airline}</TableCell>
                <TableCell
                  className={classes.flightNumberCell}
                  data-eventid={event._id}
                >
                  <Link to={`events/${event._id}`}>{event.inboundNumber}</Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableTotalCell colSpan={4}>
              Total Rows: {events.length}
            </TableTotalCell>
          </TableRow>
        </TableFooter>
      </Table>
      <Table className={classes.mainTable} size="small" stickyHeader>
        <TableHead>
          <TableRow className={classes.tableHead}>
            <TableCell>Date/ATA</TableCell>
            <TableCell>DL Time</TableCell>
            <TableCell>Route</TableCell>
            <TableCell>Aircraft Type</TableCell>
            <TableCell>Aircraft Registration</TableCell>
            <TableCell>Ops. crew</TableCell>
            <TableCell>DHC</TableCell>
            <TableCell>Total Pax</TableCell>
            <TableCell>Outbound Pax</TableCell>
            <TableCell>Transit Pax</TableCell>
            <TableCell>Cargo (kg)</TableCell>
            <TableCell>Parking no.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!events.length && <TableRowPlaceholder />}
          {events.map(event => {
            let [isDelayed, delayTime] = eh.calculateDelayTime(
              event.STA,
              event.inbound.ATA || event.inbound.ETA
            );
            const isAheadOfTime = delayTime.startsWith('-');
            if (delayTime === '00:00') delayTime = null;

            return (
              <TableRow
                selected={event._id === selectedRow}
                key={event._id}
                data-eventid={event._id}
                onClick={handleRowClick}
              >
                <TableCell noLeftBorder>
                  {dh.dateToDateTimeString(event.inbound.ATA)}
                </TableCell>
                <TableCell
                  className={classNames({
                    [classes.aheadOfTime]: isAheadOfTime,
                    [classes.delayed]: isDelayed,
                  })}
                >
                  {delayTime}
                </TableCell>
                <TableCell>{`${event.inboundAirport}-${event.airport}`}</TableCell>
                <TableCell>{event.inbound.aircraftType}</TableCell>
                <TableCell>{event.inbound.aircraftReg}</TableCell>
                <TableCell>{eh.getOpsCrewCount(event.inbound.crew)}</TableCell>
                <TableCell>
                  {eh.getDeadHeadCrewCount(event.inbound.crew)}
                </TableCell>
                <TableCell>
                  {eh.getTotalPaxLoad(event.inbound.paxLoad)}
                </TableCell>
                <TableCell>
                  {eh.getTransferCount(event.inbound.transfers.outbound)}
                </TableCell>
                <TableCell>
                  {eh.getTransferCount(event.inbound.transfers.transit)}
                </TableCell>
                <TableCell>
                  {eh.getTotalCargoWeight(event.inbound.cargo)}
                </TableCell>
                <TableCell>{event.inbound.parkingBay}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell>{total.pax}</TableTotalCell>
            <TableTotalCell>{total.transfers}</TableTotalCell>
            <TableTotalCell />
            <TableTotalCell>{total.cargoWeight}</TableTotalCell>
            <TableTotalCell />
          </TableRow>
        </TableFooter>
      </Table>
    </>
  );
};

export default InboundStatsTable;
